import { FC, useCallback, useEffect, useMemo } from 'react'
import { Alert, ColumnLayout } from '@cloudscape-design/components'
import FormInput from '../Input'
import { useForm } from 'react-hook-form'
import FormSelect from '../Select'
import { useGenericTagsManagementFormStore } from '../../../stores/tags/genericTagsManagementFormStore'
import { useAccountCreationFormStore } from '../../../stores/accounts/newAccountCreationFormStore'
import useAccounts from '../../../hooks/query/useAccounts'
import CONSTANT from '../../../constants'
 
const { DATA_CLASSIFICATION_LIST, COST_TYPE_LIST, PLATFORM_LIST } = CONSTANT
 
const GenericTagsNew: FC = () => {
    const {
        formValues,
        actions: { setFormValues, setFormValidity }
    } = useGenericTagsManagementFormStore()
 
    // Get the selected account types from the account creation flow
    const { formValues: accountCreationFormValues } = useAccountCreationFormStore()
    const selectedAccountTypes = useMemo<Array<{ label: string; value: string }>>(
        () => accountCreationFormValues.accountTypes ?? [],
        [accountCreationFormValues.accountTypes]
    )

    const {
        control,
        formState: { isValid },
        setValue
    } = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: formValues,
        shouldUseNativeValidation: true
    })

    // Fetch all accounts for duplicate systemAlias validation
    const { data: accounts } = useAccounts()

    // Validate that systemAlias is not already used as platformSubgroupName in any approved account
    const validateSystemAlias = useCallback(
        (value: string): boolean | string => {
            if (!value || !accounts) return true
            const normalized = value.trim().toLowerCase()

            // Find ALL existing accounts whose platformSubgroupName matches the entered alias
            const conflictingAccounts = accounts.filter((account) => {
                const existing = account.approval?.platformSubgroupName
                return existing?.trim().toLowerCase() === normalized
            })

            if (conflictingAccounts.length === 0) return true

            // Collect the types of all conflicting accounts
            const conflictingTypes = new Set(conflictingAccounts.map((a) => a.type))

            // Determine which of the currently selected types are in conflict
            const selectedTypeValues = selectedAccountTypes.map((t) => t.value)
            const conflictingSelectedTypes = selectedTypeValues.filter((t) =>
                conflictingTypes.has(t)
            )

            if (conflictingSelectedTypes.length === 0) return true

            // All selected types conflict → generic message
            if (conflictingSelectedTypes.length === selectedTypeValues.length) {
                return 'This System Alias is already in use as a Platform Subgroup Name in another approved account request.'
            }

            // Only some selected types conflict → name the specific ones
            return `This System Alias is already in use as a Platform Subgroup Name for the following account type(s): ${conflictingSelectedTypes.join(', ')}.`
        },
        [accounts, selectedAccountTypes]
    )

    const systemAliasValidationMessage = useMemo<
        | {
            type: 'error' | 'info'
            text: string
        }
        | undefined
    >(() => {
        const alias = formValues.systemAlias
        if (!alias || !accounts) return undefined

        const normalized = alias.trim().toLowerCase()

        const conflictingAccounts = accounts.filter((account) => {
            const existing = account.approval?.platformSubgroupName
            return existing?.trim().toLowerCase() === normalized
        })

        if (conflictingAccounts.length === 0) return undefined

        const conflictingTypes = Array.from(
            new Set(
                conflictingAccounts
                    .map((a) => a.type)
                    .filter((type): type is string => Boolean(type))
            )
        )

        const selectedTypeValues = selectedAccountTypes.map((t) => t.value)
        const conflictingSelectedTypes = selectedTypeValues.filter((t) =>
            conflictingTypes.includes(t)
        )

        if (conflictingSelectedTypes.length === 0) {
            return {
                type: 'info',
                text: `This System Alias already exists for ${conflictingTypes.join(
                    ', '
                )} account type(s). Reuse across different account types (including Sandbox) is allowed.`
            }
        }

        if (conflictingSelectedTypes.length === selectedTypeValues.length) {
            return {
                type: 'error',
                text: 'This System Alias is already in use as a Platform Subgroup Name in another approved account request.'
            }
        }

        return {
            type: 'error',
            text: `This System Alias is already in use for the following selected account type(s): ${conflictingSelectedTypes.join(
                ', '
            )}.`
        }
    }, [formValues.systemAlias, accounts, selectedAccountTypes])
 
    useEffect(() => {
        setFormValidity(isValid)
    }, [isValid, setFormValidity])

		useEffect(() => {
			setValue('platformName', formValues.platformName)
			setValue('systemAlias', formValues.systemAlias)
			setValue('tagProject', formValues.tagProject)
			setValue('dataClassification', formValues.dataClassification)
			setValue('fundingSourceType', formValues.fundingSourceType)
			setValue('costCenter', formValues.costCenter)
			setValue('WBSCode', formValues.WBSCode)
		}, [formValues, setValue])
 
    useEffect(() => {
        if (formValues?.fundingSourceType && formValues.fundingSourceType?.value === 'opex') {
            setValue('WBSCode', '')
			setFormValues('WBSCode', '')
        } else if (formValues?.fundingSourceType && formValues.fundingSourceType?.value === 'capex') {
            setValue('costCenter', '')
			setFormValues('costCenter', '')
        }
    }, [formValues.fundingSourceType, setValue, setFormValues])
 
    return (
        <ColumnLayout columns={2} variant="default">
            <FormSelect
                name="platformName"
                label="Platform Name"
                control={control}
				rules={{
					required: 'Please Select Option'
				}}
				setFormValues={setFormValues}
				options={PLATFORM_LIST}
				placeholder="Select Platform "
			/>
			<FormInput
				name="tagProject"
				label="Project"
				type="text"
				control={control}
				rules={{
					required: `Please enter valid value, Example: LUNA, JANUS, VIASH, UDG1:PH2`,
					pattern: {
						//value: /^[A-Z0-9]+(?::(PH|FS|UC)[A-Z0-9]{2,})?(?:-(TR|CR|DR)[A-Z0-9]{2,})?$/g,
						value: /^[A-Z0-9]+(?::[A-Z0-9]+)?(?:-[A-Z0-9]+)*$/g,
						message:
							'Please enter valid value, Example: LUNA, JANUS, VIASH, UDG1:PH2'
					},
					minLength: {
						value: 3,
						message: 'Minimum length should be 3'
					}
				}}
				setFormValues={setFormValues}
				placeholder="Example: LUNA, JANUS, VIASH, UDG1:PH2"
			/>
			{/* <FormSelect
				label="Platform Repo Type"
				name="platformRepo"
				control={control}
				rules={{
					required: 'Please Select Option'
				}}
				setFormValues={setFormValues}
				options={PLATFORM_REPO_LIST}
				placeholder="Select Platform Repo Type"
			/> */}
			<FormInput
				name="systemAlias"
				label="System Alias"
				type="text"
				placeholder="Example: hipms, namax"
				control={control}
				rules={{
					required: 'System alias should be 5 lowercase letters, Example: hipms',
					pattern: {
						value: /^[a-z]{5}$/,
						message: 'System alias should be 5 lowercase letters, Example: hipms'
					},
					validate: validateSystemAlias
				}}
				setFormValues={setFormValues}
			/>
            {systemAliasValidationMessage && (
                <Alert
                    type={systemAliasValidationMessage.type}
                    statusIconAriaLabel={
                        systemAliasValidationMessage.type === 'error'
                            ? 'Error'
                            : 'Info'
                    }
                >
                    {systemAliasValidationMessage.text}
                </Alert>
            )}
			<FormSelect
				label="Data Classification"
				name="dataClassification"
				control={control}
				rules={{
					required: 'Please Select Option'
				}}
				setFormValues={setFormValues}
				options={DATA_CLASSIFICATION_LIST}
				placeholder="Select Data Classification"
			/>
			<FormSelect
				label="Funding Source"
				name="fundingSourceType"
				control={control}
				rules={{
					required: 'Please Select Option'
				}}
				setFormValues={setFormValues}
				options={COST_TYPE_LIST}
				placeholder="Select Funding Source Type"
			/>
			<br />
			<FormInput
				name="WBSCode"
				label="WBS Code"
				type="text"
				control={control}
				rules={{
					pattern: {
						value: /^[A-Z0-9]{2,5}-[A-Z0-9]{1,15}-[0-9]{2}(?:-[A-Z0-9]{2,})?(?:-[A-Z0-9]{2,})?$/g,
						message: 'Please Enter all in CAPS, Ex. ISG-ANODOT-23, EDO-ARROW-23, ISG-PROJECTA-23, EDO1-PROJECTA-24, EDO-PROJECTC-24-SW-01'
					},
					minLength: {
						value: 3,
						message: 'Minimum length should be 3'
					}
				}}
				setFormValues={setFormValues}
				placeholder="Example: ISG-ANODOT-23, EDO-ARROW-23, ISG-PROJECTA-23, EDO1-PROJECTA-24, EDO-PROJECTC-24-SW-01"
				disabled={
					formValues?.fundingSourceType &&
					formValues.fundingSourceType.value === 'opex'
				}
			/>
			<FormInput
				name="costCenter"
				label="Cost Center"
				type="text"
				control={control}
				rules={{
					pattern: {
						value: /^[A-Z0-9]{1,5}-[A-Z0-9]{1,3}$/g,
						message: 'Please Enter all in CAPS, Ex. ISG12-M2, EDOM1-24, EGA-A12, ISGM1-H1'
					},
					minLength: {
						value: 3,
						message: 'Minimum length should be 3'
					}
				}}
				setFormValues={setFormValues}
				placeholder="Example: ISG12-M2, EDOM1-24, EGA-A12, ISGM1-H1"
				disabled={
					formValues?.fundingSourceType &&
					formValues.fundingSourceType.value === 'capex'
				}
			/>
		</ColumnLayout>
	)
}
 
export default GenericTagsNew